/**
 * ActivityStream — die Achse als EINE Tabelle mit drei Bändern.
 *
 * Bewusst nicht drei Karten nebeneinander und erst recht kein Tab-Register:
 * die Slot-Kopfzeile steht genau einmal ganz oben, und alle Zeilen — geplanter
 * Autorun, laufender Job, alter Run — liegen mit ihren neun Slots untereinander
 * in derselben Spaltenordnung wie auf der Runs- und der Queue-Seite. Erst
 * dadurch ist „was" quer über die drei Quellen vergleichbar.
 *
 * Die Bandkopfzeilen sind die Zeitmarken der Achse (Geplant · Jetzt ·
 * Verlauf); zwischen „Geplant" und „Verlauf" liegt die Naht *jetzt*. Ein Band
 * verschwindet nie — es behält seine Kopfzeile und sagt in einem Satz, warum
 * es leer ist. Das ist dieselbe Regel wie im leeren Queue-Tab: eine Struktur,
 * die je nach Datenlage auf- und zuklappt, kann man nicht lesen lernen.
 */
import type { ReactNode } from "react";
import { Button, Card, Icon } from "@conuti-das/prince-ui";
import { princeIcon } from "@/ui-prince";
import { MicroFlowHeader, MicroFlowRow } from "@/components/flow";
import { STAGE_SLOTS } from "@/components/queue/stageChain";
import { EXPLAINED_SLOTS } from "./stations";
import { formatAbsolute, formatRelative } from "./timeline";
import type { ActivityBandId, ActivityEntry } from "./timeline";
import "./activity.css";

export interface ActivityBand {
  id: ActivityBandId;
  /** Beschriftung der Zeitmarke, z. B. „Jetzt". */
  title: string;
  /** Halbsatz neben der Beschriftung — was in diesem Band steht. */
  hint?: ReactNode;
  /** Satz für den leeren Fall: „lädt", „nichts da" oder „Runner offline". */
  note?: ReactNode;
  /** Satz, der IMMER steht — z. B. ein misslungener Abbruch. */
  message?: ReactNode;
  /** Auslöser, der in dieses Band gehört (z. B. „Neuer Autorun"). */
  action?: ReactNode;
  entries: ActivityEntry[];
}

export interface ActivityStreamProps {
  bands: ActivityBand[];
  /** Bezugspunkt der Relativzeiten — als Prop, damit Tests ohne Uhr auskommen. */
  now: number;
  /** Zeilen ohne Route (Autoruns) melden sich hier zur Pflege. */
  onOpenEntry?: (entry: ActivityEntry) => void;
  /** Abbrechen eines laufenden oder wartenden Jobs. Ohne Handler kein Knopf. */
  onCancel?: (entry: ActivityEntry) => void;
  /** Eintrag, dessen Abbruch gerade unterwegs ist. */
  cancelBusyKey?: string | null;
  /** Runner gilt als nicht erreichbar — der Knopf griffe ins Leere. */
  cancelDisabled?: boolean;
}

interface RowProps {
  entry: ActivityEntry;
  now: number;
  onOpenEntry?: (entry: ActivityEntry) => void;
  onCancel?: (entry: ActivityEntry) => void;
  cancelBusyKey?: string | null;
  cancelDisabled?: boolean;
}

function ActivityRow({
  entry,
  now,
  onOpenEntry,
  onCancel,
  cancelBusyKey,
  cancelDisabled,
}: RowProps) {
  const when = formatAbsolute(entry.at);
  const relative = formatRelative(entry.at, now);

  return (
    <MicroFlowRow
      slots={STAGE_SLOTS}
      cells={entry.cells}
      // Der Name trägt alle drei Fragen — für Screenreader ist die Zeile damit
      // vollständig, ohne die Kette Slot für Slot abzuhören.
      label={`${entry.kindLabel} ${entry.title}, ${entry.whoRole} ${entry.who}, ${when}`}
      title={
        <>
          <span className="act-kind" data-kind={entry.kind}>
            {entry.kindLabel}
          </span>
          {entry.title}
        </>
      }
      subtitle={
        <>
          {entry.release && (
            <>
              Stand <strong>{entry.release}</strong>
              {" · "}
            </>
          )}
          <span title={entry.raw}>
            {entry.whoRole}: {entry.who}
          </span>
          {" · "}
          {when}
        </>
      }
      // Der Trail ist ein Geschwister des Zeilenkopfs, kein Kind: ein <button>
      // in einem <a> wäre so ungültig wie in einem <button>. Deshalb steht das
      // Abbrechen NEBEN der Zeile — und der Trail steht in jeder Zeile, auch
      // ohne Knopf, sonst rückten die neun Slots gegeneinander.
      trail={
        <>
          <span className="act-when">{relative}</span>
          {entry.cancellable && onCancel && (
            <Button
              variant="plain"
              size="s"
              isDisabled={cancelDisabled || cancelBusyKey === entry.key}
              onPress={() => onCancel(entry)}
            >
              <Icon name={princeIcon("decline")} size={14} />
              {cancelBusyKey === entry.key ? "bricht ab …" : "Abbrechen"}
            </Button>
          )}
        </>
      }
      // Zeilen mit Ziel sind echte Links (Mittelklick öffnet den Lauf im neuen
      // Tab); der Autorun hat keine Route und öffnet stattdessen seine Pflege.
      href={entry.href}
      onActivate={entry.href ? undefined : () => onOpenEntry?.(entry)}
      className={entry.disabled ? "act-row--off" : undefined}
    />
  );
}

export function ActivityStream({
  bands,
  now,
  onOpenEntry,
  onCancel,
  cancelBusyKey,
  cancelDisabled,
}: ActivityStreamProps) {
  return (
    <Card padding="none" className="mpf-table act-stream">
      {/* Kopfzeile mit `EXPLAINED_SLOTS`: dieselben neun Slots, aber im `title`
          steht statt des nackten Stage-Namens die Klartext-Bedeutung. Ein
          Tooltip kostet keine Fläche — die Legende hängt damit dauerhaft an den
          Kürzeln, ohne dass jemand die Selbsterklärung aufklappen muss. Die
          Zeilen behalten `STAGE_SLOTS`: im schmalen Ketten-Popover wäre ein
          ganzer Satz je Slot unlesbar. */}
      <MicroFlowHeader slots={EXPLAINED_SLOTS} trail="Wann">
        Wann · wer · was
      </MicroFlowHeader>
      {bands.map((band) => (
        <section
          key={band.id}
          className="act-band-group"
          aria-labelledby={`act-band-${band.id}`}
        >
          <div className="act-band" data-band={band.id}>
            <span className="act-band-title" id={`act-band-${band.id}`}>
              {band.title}
            </span>
            <span className="act-band-count">{band.entries.length}</span>
            {band.hint !== undefined && <span className="act-band-hint">{band.hint}</span>}
            {band.action !== undefined && <span className="act-band-action">{band.action}</span>}
          </div>
          {band.message !== undefined && band.message !== null && (
            <p className="mpf-empty act-message">{band.message}</p>
          )}
          {band.entries.length === 0
            ? band.note !== undefined && <p className="mpf-empty">{band.note}</p>
            : band.entries.map((entry) => (
                <ActivityRow
                  key={entry.key}
                  entry={entry}
                  now={now}
                  onOpenEntry={onOpenEntry}
                  onCancel={onCancel}
                  cancelBusyKey={cancelBusyKey}
                  cancelDisabled={cancelDisabled}
                />
              ))}
        </section>
      ))}
    </Card>
  );
}
